import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import ROUTES from '../assets/routes'
import { Security } from '../proto/generated/security'
import { exchangeToJSON } from '../proto/generated/common'
import { setFinancialPanelOpen, setFinancialPanelSection, setSeasonalsPanelOpen, setTechnicalsPanelOpen } from '../reducer/home.reducer'
import { updatePrimarySecurityAsync } from '../reducer/home.thunks'
import { AppDispatch } from '../store'
import { FINANCIAL_SHAREHOLDING_SECTION_ID } from '../util/financialData'
import { getSecurityKey, isFnOSecurity } from '../util/security'

export type OverviewPanelId = 'seasonals' | 'technicals' | 'shareholding'

/** Opens seasonals/technicals/financials via dedicated symbol URLs. */
const useOverviewPanelNavigation = (panel: OverviewPanelId) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    return useCallback(
        async (security: Security | undefined) => {
            if (!security?.code) return

            const securityKey = getSecurityKey(security.exchange, security.code)
            const isFnO = isFnOSecurity(security)

            await updatePrimarySecurityAsync({
                key: securityKey,
                expressionMode: false,
                securities: [securityKey],
                underlyingSecurityCode: getSecurityKey(
                    security.exchange,
                    isFnO ? security.underlyingSecurityCode! : security.code
                )
            })(dispatch)

            dispatch(setSeasonalsPanelOpen(false))
            dispatch(setTechnicalsPanelOpen(false))
            dispatch(setFinancialPanelOpen(false))

            if (panel === 'shareholding') {
                dispatch(setFinancialPanelSection(FINANCIAL_SHAREHOLDING_SECTION_ID))
                navigate(
                    generatePath(ROUTES.financials, {
                        exchange: exchangeToJSON(security.exchange),
                        symbol: security.code
                    })
                )
                return
            }

            navigate(
                generatePath(panel === 'seasonals' ? ROUTES.seasonals : ROUTES.technicals, {
                    exchange: exchangeToJSON(security.exchange),
                    symbol: security.code
                })
            )
        },
        [dispatch, navigate, panel]
    )
}

export default useOverviewPanelNavigation
